// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Card, Group, Modal, Stack, Text, Title } from '@mantine/core';
import type { AppointmentTypeDefinition, AvailableTimeSlot } from '@polarissleep/scheduling';
import type { JSX } from 'react';
import type { PatientAppointmentCandidate } from '../../scheduling-next/types';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from './AppointmentTimeWithTimezone';

type BookingSelection = AvailableTimeSlot | PatientAppointmentCandidate;

interface BookingConfirmationProps {
  opened: boolean;
  appointmentType: AppointmentTypeDefinition;
  slot: BookingSelection;
  onConfirm: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  bookingEnabled?: boolean;
}

/**
 * Centered modal confirmation for patient booking.
 * Shows appointment details, "What to Expect", and reminder messaging.
 * @param root0 - Component props
 * @param root0.opened - Whether the modal is visible
 * @param root0.appointmentType - The selected appointment type
 * @param root0.slot - The selected time slot
 * @param root0.onConfirm - Callback when user confirms the booking
 * @param root0.onBack - Callback to dismiss the modal
 * @param root0.isSubmitting - Whether the booking request is in progress
 * @returns The booking confirmation modal
 */
export function BookingConfirmation({
  opened,
  appointmentType,
  slot,
  onConfirm,
  onBack,
  isSubmitting,
  bookingEnabled = true,
}: BookingConfirmationProps): JSX.Element {
  const start = 'appointment' in slot ? slot.appointment.start : slot.start;
  const end = 'appointment' in slot ? slot.appointment.end : slot.end;
  const providerName = 'appointment' in slot ? slot.providerName : undefined;
  const dateStr = formatAppointmentDate(start);
  return (
    <Modal opened={opened} onClose={onBack} size="lg" centered withCloseButton={false} padding="xl" radius="lg">
      <Stack gap="lg" align="center">
        <Title order={2}>Confirm Appointment</Title>
        <Text size="sm" c="dimmed" ta="center">
          Please make sure the following information is correct:
        </Text>

        <Card bg="gray.0" p="lg" radius="md" w="100%">
          <Stack gap="md" align="center">
            <Text fw={600} size="md">
              {appointmentType.display}
            </Text>
            <Text fw={600} size="lg">
              {dateStr}
            </Text>
            <AppointmentTimeWithTimezone
              start={start}
              end={end}
              groupProps={{ justify: 'center' }}
              textProps={{ fw: 600, size: 'lg' }}
            />

            {providerName && (
              <Text size="sm" c="dimmed">
                Provider: {providerName}
              </Text>
            )}

            {appointmentType.patientEligibility && (
              <Card withBorder p="md" radius="md" w="100%">
                <Stack gap="xs">
                  <Text fw={600} size="sm" ta="center">
                    What to Expect
                  </Text>
                  <Text size="sm" ta="center">
                    {appointmentType.patientEligibility}
                  </Text>
                </Stack>
              </Card>
            )}

            <Text size="sm" c="dimmed" ta="center">
              When you confirm this appointment, we will send you an email confirmation with the date, time, and link to
              join the video call. We will also text you two reminders, one day and fifteen minutes before your
              appointment.
            </Text>
          </Stack>
        </Card>

        <Group justify="center" w="100%">
          <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button onClick={onConfirm} loading={isSubmitting} disabled={isSubmitting || !bookingEnabled}>
            {bookingEnabled ? 'Confirm Appointment' : 'Booking will be available soon'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
